<template>
  <div>
    <div class="d-flex align-items-center mb-1">
      <b-button
        class="mr-1"
        variant="primary"
        :disabled="isDrawing"
        @click="drawNewPolygon([])"
      >
        <span>Start drawing new polygon</span>
      </b-button>
      <b-button
        variant="primary"
        :disabled="!isDrawing"
        @click="endDrawingPolygon"
      >
        <span>End drawing polygon</span>
      </b-button>
      <b-button
        class="ml-auto"
        variant="success"
        @click="emitChanges"
      >
        Save Changes
      </b-button>
    </div>

    <!-- Selected Polygon Indicator -->
    <div
      v-if="selectedPolygonId"
      class="mb-2 alert alert-info"
    >
      <strong>Selected Polygon:</strong> {{ getSelectedPolygonDisplayName() }}
      <small class="ml-2 text-muted">(Click on a polygon on the map or in the form below to select it)</small>
    </div>

    <!-- Polygon Names List -->
    <div
      v-if="polygonNames.length"
      class="mb-3"
    >
      <h6>Polygon Names:</h6>
      <div
        v-for="(polygonName, index) in polygonNames"
        :key="polygonName.id"
        class="mb-2 p-2 border rounded"
        :class="{
          'border-success bg-light': selectedPolygonId === polygonName.id,
          'border-primary': selectedPolygonId !== polygonName.id
        }"
        style="cursor: pointer;"
        @click="selectPolygon(polygonName.id)"
      >
        <b-row>
          <b-col md="5">
            <b-form-group
              :label="`Polygon ${index + 1} Name (English)`"
              :label-for="`polygon-name-${polygonName.id}`"
            >
              <b-form-input
                :id="`polygon-name-${polygonName.id}`"
                v-model="polygonName.name"
                type="text"
                placeholder="Enter English name"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="5">
            <b-form-group
              :label="`Polygon ${index + 1} Name (Arabic)`"
              :label-for="`polygon-name-ar-${polygonName.id}`"
            >
              <b-form-input
                :id="`polygon-name-ar-${polygonName.id}`"
                v-model="polygonName.name_ar"
                type="text"
                placeholder="Enter Arabic name"
                required
              />
            </b-form-group>
          </b-col>
          <b-col
            md="2"
            class="d-flex align-items-end"
          >
            <b-button
              variant="danger"
              size="sm"
              @click="removePolygon(index)"
            >
              Remove
            </b-button>
          </b-col>
        </b-row>
      </div>
    </div>

    <div
      ref="mapRef"
      class="map"
    />
  </div>
</template>

<script>
import {
  BButton, BRow, BCol, BFormGroup, BFormInput,
} from 'bootstrap-vue'
import {
  reactive, ref, onMounted, watch,
} from '@vue/composition-api'
import { successNotification, errorNotification } from '@/utils/toast-notifications'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
  },
  props: {
    initialCoords: {
      type: Array,
      default: () => [],
    },
    autoEmit: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, ctx) {
    const parsePolygonPathsToJsonArray = polygon => {
      const polygonArr = polygon.getPaths().getArray()
      const temp = polygonArr[0]
      const arr = []
      if (!temp?.length) {
        return []
      }
      for (let i = 0; i <= temp.length - 1; i += 1) {
        arr.push({
          lat: temp.getAt(i).lat(),
          lng: temp.getAt(i).lng(),
        })
      }
      return arr
    }
    const isDrawing = ref(false)
    const mapClickHandler = ref()
    const localPolygons = ref([])
    const polygonNames = ref([])
    const mapRef = ref(null)
    const selectedPolygonId = ref(null)

    const map = ref()
    const mapCenter = reactive({
      lat: 31.963158, lng: 35.930359,
    })

    const updatePolygonStyles = () => {
      localPolygons.value.forEach(polygon => {
        const isSelected = polygon.get('id') === selectedPolygonId.value
        polygon.setOptions({
          fillColor: isSelected ? '#28a745' : '#007bff',
          fillOpacity: isSelected ? 0.5 : 0.3,
          strokeColor: isSelected ? '#28a745' : '#007bff',
          strokeWeight: isSelected ? 3 : 2,
        })
      })
    }

    function initMap() {
      // eslint-disable-next-line no-undef
      map.value = new google.maps.Map(mapRef.value, {
        zoom: 10,
        center: mapCenter,
      })
      map.value.setOptions({ draggableCursor: 'pointer' })

      // Add click listener to map for clearing selection
      // eslint-disable-next-line no-undef
      google.maps.event.addListener(map.value, 'click', e => {
        // Only clear selection if not drawing and not clicking on a polygon
        if (!isDrawing.value && !e.vertex) {
          selectedPolygonId.value = null
          updatePolygonStyles()
        }
      })
    }

    const autoEmitChanges = () => {
      // Only auto-emit if enabled via prop
      if (!props.autoEmit) return

      // Auto-emit changes without validation for real-time updates
      const multiGeofence = localPolygons.value.map(polygon => {
        const polygonId = polygon.get('id')
        const nameObj = polygonNames.value.find(name => name.id === polygonId)
        const dropoff = parsePolygonPathsToJsonArray(polygon).map(coord => ({
          lat: coord.lat.toString(),
          lon: coord.lng.toString(),
        }))
        return {
          target_type: 'geofence',
          name: nameObj?.name || '',
          name_ar: nameObj?.name_ar || '',
          dropoff,
        }
      })

      const formattedTarget = [
        {
          target_type: 'multi_geofence',
          multi_geofence: multiGeofence,
        },
      ]

      ctx.emit('path-set', formattedTarget)
    }

    const selectPolygon = polygonId => {
      selectedPolygonId.value = polygonId
      updatePolygonStyles()
    }

    const getSelectedPolygonDisplayName = () => {
      if (!selectedPolygonId.value) return ''
      const nameObj = polygonNames.value.find(name => name.id === selectedPolygonId.value)
      if (!nameObj) return 'Unknown Polygon'

      const englishName = nameObj.name || 'Unnamed'
      const arabicName = nameObj.name_ar || 'غير مسمى'
      const index = polygonNames.value.findIndex(name => name.id === selectedPolygonId.value) + 1

      return `Polygon ${index} (${englishName} / ${arabicName})`
    }

    const drawNewPolygon = (polygonPaths, existingName = '', existingNameAr = '') => {
      if (!polygonPaths.length) {
        isDrawing.value = true
      }
      // eslint-disable-next-line no-undef
      const polygon = new google.maps.Polygon({
        paths: polygonPaths && polygonPaths.length ? polygonPaths : [],
        map: map.value,
        editable: true,
        clickable: true,
        fillColor: '#007bff',
        fillOpacity: 0.3,
        strokeColor: '#007bff',
        strokeWeight: 2,
      })
      const polygonId = Math.random().toString()
      polygon.set('id', polygonId)

      // Add polygon name entry
      polygonNames.value.push({
        id: polygonId,
        name: existingName,
        name_ar: existingNameAr,
      })

      // eslint-disable-next-line no-undef
      google.maps.event.addListener(polygon, 'click', e => {
        selectPolygon(polygonId)
        // Prevent map click event from firing
        if (e.stop) {
          e.stop()
        }
      })

      // eslint-disable-next-line no-undef
      google.maps.event.addListener(polygon, 'rightclick', () => {
        polygon.setMap(null)
        const polygonIndex = localPolygons.value.findIndex(el => el.get('id') === polygonId)
        const nameIndex = polygonNames.value.findIndex(el => el.id === polygonId)
        if (polygonIndex !== -1) localPolygons.value.splice(polygonIndex, 1)
        if (nameIndex !== -1) polygonNames.value.splice(nameIndex, 1)
        if (selectedPolygonId.value === polygonId) {
          selectedPolygonId.value = null
        }
        autoEmitChanges()
      })

      localPolygons.value.push(polygon)
      if (!polygonPaths.length) {
        mapClickHandler.value = e => {
          const coordinate = e.latLng.toJSON()
          const oldPath = parsePolygonPathsToJsonArray(polygon)
          polygon.setPaths([...oldPath, coordinate])
        }
        map.value.addListener('click', mapClickHandler.value)
      }
    }
    const endDrawingPolygon = () => {
      isDrawing.value = false
      // eslint-disable-next-line no-undef
      google.maps.event.clearListeners(map.value, 'click')
      autoEmitChanges()
    }

    const emitChanges = () => {
      // Validate that all polygons have names
      const missingNames = polygonNames.value.some(nameObj => !nameObj.name || !nameObj.name_ar)
      if (missingNames) {
        errorNotification('Please provide both English and Arabic names for all polygons')
        return
      }

      const multiGeofence = localPolygons.value.map(polygon => {
        const polygonId = polygon.get('id')
        const nameObj = polygonNames.value.find(name => name.id === polygonId)
        const dropoff = parsePolygonPathsToJsonArray(polygon).map(coord => ({
          lat: coord.lat.toString(),
          lon: coord.lng.toString(),
        }))
        return {
          target_type: 'geofence',
          name: nameObj?.name || '',
          name_ar: nameObj?.name_ar || '',
          dropoff,
        }
      })

      const formattedTarget = [
        {
          target_type: 'multi_geofence',
          multi_geofence: multiGeofence,
        },
      ]

      ctx.emit('path-set', formattedTarget)
      successNotification('Changes saved')
    }
    const initPolygons = () => {
      if (
        props.initialCoords?.[0]?.target_type === 'multi_geofence'
    && Array.isArray(props.initialCoords[0].multi_geofence)
      ) {
        props.initialCoords[0].multi_geofence.forEach(geofence => {
          const path = geofence.dropoff.map(coord => ({
            lat: parseFloat(coord.lat),
            lng: parseFloat(coord.lon),
          }))
          drawNewPolygon(path, geofence.name || '', geofence.name_ar || '')
        })
        // Auto-emit the initially loaded polygons
        autoEmitChanges()
      }
    }

    const removePolygon = index => {
      const polygonName = polygonNames.value[index]
      if (polygonName) {
        const polygon = localPolygons.value.find(p => p.get('id') === polygonName.id)
        if (polygon) {
          polygon.setMap(null)
          const polygonIndex = localPolygons.value.findIndex(p => p.get('id') === polygonName.id)
          if (polygonIndex !== -1) localPolygons.value.splice(polygonIndex, 1)
        }
        polygonNames.value.splice(index, 1)
        autoEmitChanges()
      }
    }

    // Watch for changes in polygon names and auto-emit
    watch(
      () => polygonNames.value,
      () => {
        if (props.autoEmit) {
          autoEmitChanges()
        }
      },
      { deep: true },
    )

    onMounted(() => {
      initMap()
      if (props.initialCoords.length) {
        initPolygons()
      }
    })
    return {
      isDrawing,
      drawNewPolygon,
      mapRef,
      emitChanges,
      localPolygons,
      polygonNames,
      endDrawingPolygon,
      removePolygon,
      selectedPolygonId,
      selectPolygon,
      getSelectedPolygonDisplayName,
    }
  },
}

</script>

<style scoped>
.map {
  width: 100%;
  height: 400px;
}
</style>
